// One type's page, `stores/<store>/types/<type>` (02-DESIGN/10-state/
// 01-stores-types-and-instances.md § the console: Stores; 01-the-console.md
// § the address, § the moves, page by page: a type's schema shows its fields
// counted with *Show more*; hq decision 0069): the type as a readable record —
// what an item holds, its history, what it may hold, the changes it admits —
// with *Revise* as the page's primary action, the form in the page's place
// with *Edit as JSON* beside its title (DefineType.tsx). Each revision is
// recorded, never rewritten. The crumb goes back to the store's Settings tab;
// the store and every type a child names are links to their pages.

import { useState } from "react";
import { Link, useParams } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { isRefusal } from "../../platform/bus.ts";
import { Button, Empty, NotAllowed, Notice, PageHead, Pending, useLoad } from "../../ui/components.tsx";
import { useFormState } from "../../ui/form.tsx";
import { NameLink } from "../../ui/ident.tsx";
import { ShowMore } from "../../ui/more.tsx";
import { Code, Effect, HistoryPolicy, validName, validStoreName, type NamedType } from "../../wire/state.ts";
import { useSurface } from "../surface.tsx";
import { SETTINGS_TAB, storeAddress, typeAddress } from "./addresses.ts";
import { DefineType } from "./DefineType.tsx";
import { EFFECT_WORDS, fieldsLine, HISTORY_WORDS } from "./typeForm.ts";

export function TypePage() {
  const { store = "", type = "" } = useParams();
  const s = useSurface();
  if (!validStoreName(store) || !validName(type)) {
    return (
      <PageHead
        crumb={<Link to={tenantPath(s.tenant, "stores")}>Stores</Link>}
        title="Not a type"
        line={`"${type}" is not a type's name in "${store}".`}
      />
    );
  }
  return <One key={`${store}/${type}`} store={store} type={type} />;
}

/** fieldNames are the top-level fields a type's schema names, in its order; none where it names none. */
export function fieldNames(schema: unknown): string[] {
  if (typeof schema !== "object" || schema === null) {
    return [];
  }
  const props = (schema as { properties?: unknown }).properties;
  return typeof props === "object" && props !== null ? Object.keys(props) : [];
}

function One({ store, type }: { store: string; type: string }) {
  const s = useSurface();
  const form = useFormState<"revise">();
  const [tick, setTick] = useState(0);
  const [types] = useLoad(`store/${store}/types/page/${type}`, () => s.bus.stores().listTypes(store), tick);
  const [note, setNote] = useState<string | undefined>(undefined);
  const [schemaOpen, setSchemaOpen] = useState(false);
  const crumb = (
    <>
      <Link to={tenantPath(s.tenant, "stores")}>Stores</Link>
      <span>/</span>
      <Link to={storeAddress(s.tenant, store)} state={{ tab: SETTINGS_TAB }} className="mono">
        {store}
      </Link>
      <span>/</span>
      <span className="mono">{type}</span>
    </>
  );
  if (types.state === "pending") {
    return (
      <>
        <div className="crumb">{crumb}</div>
        <Pending what="Reading the type" />
      </>
    );
  }
  const t = types.state === "ready" ? types.value.find((x) => x.name === type) : undefined;
  if (types.state === "refused" || t === undefined) {
    return (
      <>
        <PageHead crumb={crumb} title={type} />
        {types.state === "refused" && !isRefusal(types.err, Code.notFound) ? (
          <NotAllowed err={types.err} what="This type" />
        ) : (
          <Empty>No type by this name in {store}.</Empty>
        )}
      </>
    );
  }
  // *Revise* replaces the page with its form (01-the-console.md § conventions: a form).
  if (form.open === "revise") {
    return (
      <DefineType
        store={store}
        existing={t}
        types={types.value}
        back={type}
        onClose={() => {
          // Read the type again, so a revision someone else saved is what the next Revise starts from.
          form.close();
          setTick((n) => n + 1);
        }}
        onDefined={() => {
          form.close();
          // The reply's revision is the store's, not the type's: it is not said (hq issue 177).
          setNote(
            `Revised ${type}. The store's current state is worked out again from its history under it.`,
          );
          setTick((n) => n + 1);
        }}
      />
    );
  }
  const fields = fieldNames(t.schema);
  const children = Object.entries(t.children ?? {});
  const changes = Object.entries(t.operations ?? {});
  return (
    <>
      <PageHead
        crumb={crumb}
        title={type}
        line={
          <>
            a type in <NameLink to={storeAddress(s.tenant, store)}>{store}</NameLink>
          </>
        }
        action={
          <Button
            kind="primary"
            onClick={() => {
              form.show("revise");
            }}
          >
            Revise
          </Button>
        }
      />
      {note !== undefined && (
        <Notice kind="ok">
          {note}{" "}
          <button
            type="button"
            className="text-link"
            onClick={() => {
              setNote(undefined);
            }}
          >
            Dismiss
          </button>
        </Notice>
      )}
      <section className="card">
        <TypeFacts
          t={t}
          fields={fields}
          children={children}
          changes={changes}
          schemaOpen={schemaOpen}
          onSchema={setSchemaOpen}
          typeTo={(name) => typeAddress(s.tenant, store, name)}
        />
      </section>
    </>
  );
}

/** TypeFacts is the type as a readable record: what an item holds counted first, its history, what it may hold, its changes. */
function TypeFacts({
  t,
  fields,
  children,
  changes,
  schemaOpen,
  onSchema,
  typeTo,
}: {
  t: NamedType;
  fields: string[];
  children: [string, string][];
  changes: [string, { effect?: string }][];
  schemaOpen: boolean;
  onSchema: (open: boolean) => void;
  typeTo: (name: string) => string;
}) {
  return (
    <dl className="kv">
      <dt>An item holds</dt>
      <dd>
        {t.schema === undefined ? (
          <span className="faint">anything: no schema</span>
        ) : (
          <>
            {fieldsLine(fields)} <ShowMore open={schemaOpen} onToggle={onSchema} />
            {schemaOpen && <pre className="json small">{JSON.stringify(t.schema, null, 2)}</pre>}
          </>
        )}
      </dd>
      <dt>History</dt>
      <dd>{HISTORY_WORDS[t.history ?? HistoryPolicy.compactable]}</dd>
      <dt>May hold</dt>
      <dd>
        {children.length === 0 ? (
          <span className="faint">nothing</span>
        ) : (
          children.map(([k, v]) => (
            <div key={k}>
              <span className="mono">{k}</span>
              <span className="small muted"> of type </span>
              <NameLink to={typeTo(v)}>{v}</NameLink>
            </div>
          ))
        )}
      </dd>
      <dt>Changes</dt>
      <dd>
        {changes.length === 0 ? (
          <span className="faint">none declared</span>
        ) : (
          changes.map(([k, op]) => (
            <div key={k}>
              <span className="mono">{k}</span>
              <span className="small muted">
                {" "}
                · {/* An absent effect reads as recorded only. */}
                {EFFECT_WORDS[op.effect === Effect.merge ? Effect.merge : Effect.none]}
              </span>
            </div>
          ))
        )}
      </dd>
    </dl>
  );
}
